import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Shield, Plus, Edit, Trash2, Package, FileText, ShoppingCart, TrendingUp, AlertTriangle } from 'lucide-react';
import DateInput from '@/components/DateInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SaludSeguridadTab({ user, onLogout }) {
  const [materiales, setMateriales] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [entradasInventario, setEntradasInventario] = useState([]);
  const [materialesBajoStock, setMaterialesBajoStock] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para Material
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    nombre: '',
    descripcion: '',
    categoria: 'EPI',
    existencias: 0,
    unidad: 'unidad',
    stock_minimo: 10,
    proveedor: '',
  });

  // Estados para Entrega
  const [entregaDialogOpen, setEntregaDialogOpen] = useState(false);
  const [entregaForm, setEntregaForm] = useState({
    trabajador_id: '',
    fecha_entrega: new Date().toISOString().split('T')[0],
    material_id: '',
    cantidad: 1,
    usuario_entrega: user.full_name || user.username,
    firma_trabajador: '',
  });

  // Estados para Entrada de Inventario
  const [entradaDialogOpen, setEntradaDialogOpen] = useState(false);
  const [entradaForm, setEntradaForm] = useState({
    material_id: '',
    cantidad: 0,
    proveedor: '',
    costo_unitario: 0,
    costo_total: 0,
    fecha_entrada: new Date().toISOString().split('T')[0],
    numero_factura: '',
    notas: '',
    usuario_registro: user.full_name || user.username,
  });
  
  // Estados para compra múltiple
  const [productosCompra, setProductosCompra] = useState([]);
  const [productoActual, setProductoActual] = useState({
    material_id: '',
    cantidad: 0,
    costo_unitario: 0,
    costo_total: 0,
  });

  // Estados para historial por trabajador
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [trabajadorSeleccionado, setTrabajadorSeleccionado] = useState(null);
  const [entregasHistorial, setEntregasHistorial] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [materialesRes, entregasRes, trabajadoresRes, entradasRes, bajoStockRes] = await Promise.all([
        axios.get(`${API}/materiales`),
        axios.get(`${API}/entregas-epi`),
        axios.get(`${API}/trabajadores`),
        axios.get(`${API}/entradas-inventario`),
        axios.get(`${API}/materiales/stock-bajo`),
      ]);
      setMateriales(materialesRes.data);
      setEntregas(entregasRes.data);
      setTrabajadores(trabajadoresRes.data);
      setEntradasInventario(entradasRes.data);
      setMaterialesBajoStock(bajoStockRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNCIONES MATERIALES =====
  const handleOpenMaterialDialog = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setMaterialForm({
        nombre: material.nombre,
        descripcion: material.descripcion || '',
        categoria: material.categoria,
        existencias: material.existencias,
        unidad: material.unidad,
        stock_minimo: material.stock_minimo || 10,
        proveedor: material.proveedor || '',
      });
    } else {
      setEditingMaterial(null);
      setMaterialForm({
        nombre: '',
        descripcion: '',
        categoria: 'EPI',
        existencias: 0,
        unidad: 'unidad',
        stock_minimo: 10,
        proveedor: '',
      });
    }
    setMaterialDialogOpen(true);
  };

  const handleSaveMaterial = async () => {
    try {
      if (editingMaterial) {
        await axios.put(`${API}/materiales/${editingMaterial.id}`, materialForm);
        toast.success('Material actualizado exitosamente');
      } else {
        await axios.post(`${API}/materiales`, materialForm);
        toast.success('Material creado exitosamente');
      }
      setMaterialDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar material');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('¿Está seguro de eliminar este material?')) return;
    
    try {
      await axios.delete(`${API}/materiales/${materialId}`);
      toast.success('Material eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar material');
    }
  };

  // ===== FUNCIONES ENTREGAS =====
  const handleOpenEntregaDialog = () => {
    setEntregaForm({
      trabajador_id: '',
      fecha_entrega: new Date().toISOString().split('T')[0],
      material_id: '',
      cantidad: 1,
      usuario_entrega: user.full_name || user.username,
      firma_trabajador: '',
    });
    setEntregaDialogOpen(true);
  };

  const handleSaveEntrega = async () => {
    try {
      // Validaciones
      if (!entregaForm.trabajador_id || !entregaForm.material_id || !entregaForm.firma_trabajador) {
        toast.error('Por favor complete todos los campos');
        return;
      }

      // Obtener nombres
      const trabajador = trabajadores.find(t => t.id === entregaForm.trabajador_id);
      const material = materiales.find(m => m.id === entregaForm.material_id);

      const entregaData = {
        ...entregaForm,
        trabajador_nombre: trabajador.nombre_completo,
        material_nombre: material.nombre,
      };

      await axios.post(`${API}/entregas-epi`, entregaData);
      toast.success('Entrega registrada exitosamente');
      setEntregaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar entrega');
    }
  };

  const handleDeleteEntrega = async (entregaId) => {
    if (!window.confirm('¿Eliminar esta entrega? El material volverá al inventario.')) return;
    
    try {
      await axios.delete(`${API}/entregas-epi/${entregaId}`);
      toast.success('Entrega eliminada y material devuelto al inventario');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar entrega');
    }
  };

  // ===== FUNCIONES HISTORIAL =====
  const handleVerHistorialTrabajador = async (trabajadorId, trabajadorNombre) => {
    try {
      const response = await axios.get(`${API}/entregas-epi/trabajador/${trabajadorId}`);
      setEntregasHistorial(response.data);
      setTrabajadorSeleccionado(trabajadorNombre);
      setHistorialDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar historial del trabajador');
    }
  };

  // ===== FUNCIONES ENTRADAS DE INVENTARIO =====
  const handleOpenEntradaDialog = () => {
    setProductosCompra([]);
    setProductoActual({
      material_id: '',
      cantidad: 0,
      costo_unitario: 0,
      costo_total: 0,
    });
    setEntradaForm({
      material_id: '',
      cantidad: 0,
      proveedor: '',
      costo_unitario: 0,
      costo_total: 0,
      fecha_entrada: new Date().toISOString().split('T')[0],
      numero_factura: '',
      notas: '',
      usuario_registro: user.full_name || user.username,
    });
    setEntradaDialogOpen(true);
  };

  const handleAgregarProducto = () => {
    if (!productoActual.material_id || productoActual.cantidad <= 0) {
      toast.error('Seleccione un material y cantidad válida');
      return;
    }

    const material = materiales.find(m => m.id === productoActual.material_id);
    const nuevoProducto = {
      ...productoActual,
      material_nombre: material.nombre,
      material_categoria: material.categoria,
    };

    setProductosCompra([...productosCompra, nuevoProducto]);
    setProductoActual({
      material_id: '',
      cantidad: 0,
      costo_unitario: 0,
      costo_total: 0,
    });
    toast.success('Producto agregado a la compra');
  };

  const handleEliminarProducto = (index) => {
    const nuevosProductos = productosCompra.filter((_, i) => i !== index);
    setProductosCompra(nuevosProductos);
    toast.success('Producto eliminado de la compra');
  };

  const handleSaveEntrada = async () => {
    try {
      if (productosCompra.length === 0) {
        toast.error('Agregue al menos un producto a la compra');
        return;
      }

      const compraData = {
        productos: productosCompra.map(p => ({
          material_id: p.material_id,
          material_nombre: p.material_nombre,
          cantidad: p.cantidad,
          costo_unitario: p.costo_unitario,
          costo_total: p.costo_total,
        })),
        proveedor: entradaForm.proveedor,
        fecha_entrada: entradaForm.fecha_entrada,
        numero_factura: entradaForm.numero_factura,
        notas: entradaForm.notas,
        usuario_registro: user.full_name || user.username,
      };

      await axios.post(`${API}/entradas-inventario/compra-multiple`, compraData);
      toast.success(`Compra registrada exitosamente con ${productosCompra.length} producto(s). Inventario actualizado.`);
      setEntradaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Tabs defaultValue="inventario" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="inventario" className="flex items-center">
            <Package className="w-4 h-4 mr-2" />
            Gestión de Inventario
          </TabsTrigger>
          <TabsTrigger value="entregas" className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            Entrega EPI/EPP
          </TabsTrigger>
          <TabsTrigger value="compras" className="flex items-center relative">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Lista de Compras
            {materialesBajoStock.length > 0 && (
              <Badge className="ml-2 bg-red-600" variant="destructive">{materialesBajoStock.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Historial Entradas
          </TabsTrigger>
        </TabsList>

          {/* TAB: GESTIÓN DE INVENTARIO */}
          <TabsContent value="inventario">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Inventario de Materiales EPI/EPP</CardTitle>
                <Button onClick={() => handleOpenMaterialDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Material
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Existencias</TableHead>
                      <TableHead>Unidad</TableHead>
                      <TableHead>Stock Mínimo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materiales.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500">
                          No hay materiales registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      materiales.map((material) => (
                        <TableRow key={material.id}>
                          <TableCell className="font-medium">{material.nombre}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{material.categoria}</Badge>
                          </TableCell>
                          <TableCell className="font-bold">{material.existencias}</TableCell>
                          <TableCell>{material.unidad}</TableCell>
                          <TableCell>{material.stock_minimo}</TableCell>
                          <TableCell>
                            {material.existencias <= material.stock_minimo ? (
                              <Badge variant="destructive">Stock Bajo</Badge>
                            ) : (
                              <Badge className="bg-green-600">Disponible</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenMaterialDialog(material)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleDeleteMaterial(material.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: ENTREGAS EPI/EPP */}
          <TabsContent value="entregas">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Registro de Entregas EPI/EPP</CardTitle>
                <Button onClick={handleOpenEntregaDialog} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Entrega
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Trabajador</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Cantidad</TableHead>
                      <TableHead>Costo Unit.</TableHead>
                      <TableHead>Costo Total</TableHead>
                      <TableHead>Entregado Por</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entregas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-gray-500">
                          No hay entregas registradas
                        </TableCell>
                      </TableRow>
                    ) : (
                      entregas.map((entrega) => (
                        <TableRow key={entrega.id}>
                          <TableCell>{entrega.fecha_entrega}</TableCell>
                          <TableCell>{entrega.trabajador_nombre}</TableCell>
                          <TableCell>{entrega.material_nombre}</TableCell>
                          <TableCell>{entrega.cantidad}</TableCell>
                          <TableCell>
                            {entrega.costo_unitario ? `$${entrega.costo_unitario.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {entrega.costo_total ? `$${entrega.costo_total.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell>{entrega.usuario_entrega}</TableCell>
                          <TableCell className="max-w-[150px] truncate">{entrega.firma_trabajador}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700"
                                onClick={() => handleVerHistorialTrabajador(entrega.trabajador_id, entrega.trabajador_nombre)}
                                title="Ver historial del trabajador"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleDeleteEntrega(entrega.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: LISTA DE COMPRAS */}
          <TabsContent value="compras">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
                    Lista de Compras - Materiales Bajo Stock Mínimo
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {materialesBajoStock.length} material(es) necesitan reabastecimiento
                  </p>
                </div>
                <Button onClick={handleOpenEntradaDialog} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Compra
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Existencias Actuales</TableHead>
                      <TableHead>Stock Mínimo</TableHead>
                      <TableHead>Cantidad Faltante</TableHead>
                      <TableHead>Cantidad Sugerida</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialesBajoStock.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-green-600">
                          ✅ Todos los materiales tienen stock suficiente
                        </TableCell>
                      </TableRow>
                    ) : (
                      materialesBajoStock.map((item) => (
                        <TableRow key={item.material.id} className="bg-yellow-50">
                          <TableCell className="font-medium">{item.material.nombre}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.material.categoria}</Badge>
                          </TableCell>
                          <TableCell className="text-red-600 font-bold">
                            {item.material.existencias} {item.material.unidad}
                          </TableCell>
                          <TableCell>{item.material.stock_minimo} {item.material.unidad}</TableCell>
                          <TableCell className="text-orange-600 font-bold">
                            -{item.cantidad_faltante} {item.material.unidad}
                          </TableCell>
                          <TableCell className="text-green-600 font-bold">
                            +{item.cantidad_sugerida} {item.material.unidad}
                          </TableCell>
                          <TableCell>{item.material.proveedor || 'Sin proveedor'}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => {
                                setEntradaForm({
                                  ...entradaForm,
                                  material_id: item.material.id,
                                  cantidad: item.cantidad_sugerida,
                                  proveedor: item.material.proveedor || '',
                                });
                                setEntradaDialogOpen(true);
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <ShoppingCart className="w-4 h-4 mr-1" />
                              Comprar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: HISTORIAL DE ENTRADAS */}
          <TabsContent value="historial">
            <Card>
              <CardHeader>
                <CardTitle>Historial de Entradas de Inventario (Compras)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Cantidad</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Costo Unitario</TableHead>
                      <TableHead>Costo Total</TableHead>
                      <TableHead>Factura</TableHead>
                      <TableHead>Registrado Por</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entradasInventario.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-500">
                          No hay entradas de inventario registradas
                        </TableCell>
                      </TableRow>
                    ) : (
                      entradasInventario.map((entrada) => (
                        <TableRow key={entrada.id}>
                          <TableCell>{entrada.fecha_entrada}</TableCell>
                          <TableCell>{entrada.material_nombre}</TableCell>
                          <TableCell className="font-bold text-green-600">+{entrada.cantidad}</TableCell>
                          <TableCell>{entrada.proveedor || '-'}</TableCell>
                          <TableCell>
                            {entrada.costo_unitario ? `$${entrada.costo_unitario.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell className="font-bold">
                            {entrada.costo_total ? `$${entrada.costo_total.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell>{entrada.numero_factura || '-'}</TableCell>
                          <TableCell>{entrada.usuario_registro}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* DIALOG: MATERIAL */}
        <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingMaterial ? 'Editar Material' : 'Nuevo Material'}
              </DialogTitle>
              <DialogDescription>
                Complete la información del material/EPI/EPP
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={materialForm.nombre}
                  onChange={(e) => setMaterialForm({ ...materialForm, nombre: e.target.value })}
                  placeholder="Ej: Casco de seguridad"
                />
              </div>

              <div className="space-y-2">
                <Label>Categoría *</Label>
                <Select
                  value={materialForm.categoria}
                  onValueChange={(value) => setMaterialForm({ ...materialForm, categoria: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EPI">EPI</SelectItem>
                    <SelectItem value="EPP">EPP</SelectItem>
                    <SelectItem value="Herramienta">Herramienta</SelectItem>
                    <SelectItem value="Material">Material</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Existencias *</Label>
                <Input
                  type="number"
                  value={materialForm.existencias}
                  onChange={(e) => setMaterialForm({ ...materialForm, existencias: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Unidad *</Label>
                <Select
                  value={materialForm.unidad}
                  onValueChange={(value) => setMaterialForm({ ...materialForm, unidad: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidad">Unidad</SelectItem>
                    <SelectItem value="par">Par</SelectItem>
                    <SelectItem value="caja">Caja</SelectItem>
                    <SelectItem value="paquete">Paquete</SelectItem>
                    <SelectItem value="metro">Metro</SelectItem>
                    <SelectItem value="litro">Litro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Stock Mínimo</Label>
                <Input
                  type="number"
                  value={materialForm.stock_minimo}
                  onChange={(e) => setMaterialForm({ ...materialForm, stock_minimo: parseInt(e.target.value) || 10 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Input
                  value={materialForm.proveedor}
                  onChange={(e) => setMaterialForm({ ...materialForm, proveedor: e.target.value })}
                  placeholder="Nombre del proveedor"
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Input
                  value={materialForm.descripcion}
                  onChange={(e) => setMaterialForm({ ...materialForm, descripcion: e.target.value })}
                  placeholder="Descripción opcional"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                // Limpiar el formulario al cancelar
                setEditingMaterial(null);
                setMaterialForm({
                  nombre: '',
                  descripcion: '',
                  categoria: 'EPI',
                  existencias: 0,
                  unidad: 'unidad',
                  stock_minimo: 10,
                  proveedor: '',
                });
                setMaterialDialogOpen(false);
              }}>
                Cancelar
              </Button>
              <Button onClick={handleSaveMaterial} className="bg-emerald-600 hover:bg-emerald-700">
                {editingMaterial ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIALOG: ENTREGA */}
        <Dialog open={entregaDialogOpen} onOpenChange={setEntregaDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Registrar Entrega de EPI/EPP</DialogTitle>
              <DialogDescription>
                Complete todos los campos para registrar la entrega
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Trabajador *</Label>
                <Select
                  value={entregaForm.trabajador_id}
                  onValueChange={(value) => setEntregaForm({ ...entregaForm, trabajador_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un trabajador" />
                  </SelectTrigger>
                  <SelectContent>
                    {trabajadores.map((trab) => (
                      <SelectItem key={trab.id} value={trab.id}>
                        {trab.nombre_completo} - {trab.numero_identificacion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fecha de Entrega *</Label>
                <DateInput
                  value={entregaForm.fecha_entrega}
                  onChange={(value) => setEntregaForm({ ...entregaForm, fecha_entrega: value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Usuario que Entrega</Label>
                <Input
                  value={entregaForm.usuario_entrega}
                  onChange={(e) => setEntregaForm({ ...entregaForm, usuario_entrega: e.target.value })}
                  disabled
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label>Herramienta/Material/EPI/EPP *</Label>
                <Select
                  value={entregaForm.material_id}
                  onValueChange={(value) => setEntregaForm({ ...entregaForm, material_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materiales
                      .filter(m => m.existencias > 0)
                      .map((mat) => (
                        <SelectItem key={mat.id} value={mat.id}>
                          {mat.nombre} - {mat.categoria} (Disponible: {mat.existencias} {mat.unidad})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Cantidad *</Label>
                <Input
                  type="number"
                  min="1"
                  value={entregaForm.cantidad}
                  onChange={(e) => setEntregaForm({ ...entregaForm, cantidad: parseInt(e.target.value) || 1 })}
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label>Firma del Trabajador (Nombre) *</Label>
                <Input
                  value={entregaForm.firma_trabajador}
                  onChange={(e) => setEntregaForm({ ...entregaForm, firma_trabajador: e.target.value })}
                  placeholder="El trabajador escribe su nombre como firma"
                />
                <p className="text-xs text-gray-500">Por ahora escriba el nombre. Próximamente habrá firma digital.</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                // Limpiar el formulario al cancelar
                setEntregaForm({
                  trabajador_id: '',
                  fecha_entrega: new Date().toISOString().split('T')[0],
                  material_id: '',
                  cantidad: 1,
                  usuario_entrega: user.full_name || user.username,
                  firma_trabajador: '',
                });
                setEntregaDialogOpen(false);
              }}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEntrega} className="bg-emerald-600 hover:bg-emerald-700">
                Registrar Entrega
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIALOG: HISTORIAL DE ENTREGAS POR TRABAJADOR */}
        <Dialog open={historialDialogOpen} onOpenChange={setHistorialDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Historial de Entregas - {trabajadorSeleccionado}</DialogTitle>
              <DialogDescription>
                Todas las entregas de EPI/EPP realizadas a este trabajador
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Resumen de Costos */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Total Entregas</p>
                  <p className="text-2xl font-bold">{entregasHistorial.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Ítems</p>
                  <p className="text-2xl font-bold">
                    {entregasHistorial.reduce((sum, e) => sum + e.cantidad, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Costo Total</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${entregasHistorial
                      .reduce((sum, e) => sum + (e.costo_total || 0), 0)
                      .toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Tabla de Historial */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Cantidad</TableHead>
                    <TableHead>Costo Unit.</TableHead>
                    <TableHead>Costo Total</TableHead>
                    <TableHead>Entregado Por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entregasHistorial.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500">
                        No hay entregas registradas para este trabajador
                      </TableCell>
                    </TableRow>
                  ) : (
                    entregasHistorial.map((entrega) => (
                      <TableRow key={entrega.id}>
                        <TableCell>{entrega.fecha_entrega}</TableCell>
                        <TableCell>{entrega.material_nombre}</TableCell>
                        <TableCell>{entrega.cantidad}</TableCell>
                        <TableCell>
                          {entrega.costo_unitario ? `$${entrega.costo_unitario.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="font-semibold">
                          {entrega.costo_total ? `$${entrega.costo_total.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>{entrega.usuario_entrega}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setHistorialDialogOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DIALOG: ENTRADA DE INVENTARIO (COMPRA) */}
        <Dialog open={entradaDialogOpen} onOpenChange={setEntradaDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Registrar Entrada de Inventario (Compra)</DialogTitle>
              <DialogDescription>
                Complete la información de la compra o reabastecimiento
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Información General de la Compra */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-sm">Información de la Compra</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Fecha de Entrada *</Label>
                    <DateInput
                      value={entradaForm.fecha_entrada}
                      onChange={(value) => setEntradaForm({ ...entradaForm, fecha_entrada: value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número de Factura</Label>
                    <Input
                      value={entradaForm.numero_factura}
                      onChange={(e) => setEntradaForm({ ...entradaForm, numero_factura: e.target.value })}
                      placeholder="Ej: FACT-001"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Proveedor</Label>
                    <Input
                      value={entradaForm.proveedor}
                      onChange={(e) => setEntradaForm({ ...entradaForm, proveedor: e.target.value })}
                      placeholder="Nombre del proveedor"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notas</Label>
                    <Input
                      value={entradaForm.notas}
                      onChange={(e) => setEntradaForm({ ...entradaForm, notas: e.target.value })}
                      placeholder="Observaciones adicionales"
                    />
                  </div>
                </div>
              </div>

              {/* Agregar Productos */}
              <div className="border-2 border-dashed border-gray-300 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-sm flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Productos a la Compra
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2 col-span-2">
                    <Label>Material *</Label>
                    <Select
                      value={productoActual.material_id}
                      onValueChange={(value) => setProductoActual({ ...productoActual, material_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un material" />
                      </SelectTrigger>
                      <SelectContent>
                        {materiales.map((mat) => (
                          <SelectItem key={mat.id} value={mat.id}>
                            {mat.nombre} - {mat.categoria} (Stock: {mat.existencias})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cantidad *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={productoActual.cantidad}
                      onChange={(e) => {
                        const cantidad = parseInt(e.target.value) || 0;
                        const costoTotal = cantidad * (productoActual.costo_unitario || 0);
                        setProductoActual({ 
                          ...productoActual, 
                          cantidad,
                          costo_total: costoTotal 
                        });
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Costo Unitario</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productoActual.costo_unitario}
                      onChange={(e) => {
                        const costoUnitario = parseFloat(e.target.value) || 0;
                        const costoTotal = costoUnitario * (productoActual.cantidad || 0);
                        setProductoActual({ 
                          ...productoActual, 
                          costo_unitario: costoUnitario,
                          costo_total: costoTotal 
                        });
                      }}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Costo Total</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productoActual.costo_total}
                      disabled
                      className="bg-gray-100"
                      placeholder="Calculado automáticamente"
                    />
                  </div>

                  <div className="col-span-2">
                    <Button 
                      type="button"
                      onClick={handleAgregarProducto}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Producto
                    </Button>
                  </div>
                </div>
              </div>

              {/* Lista de Productos Agregados */}
              {productosCompra.length > 0 && (
                <div className="border border-gray-300 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-sm">
                    Productos en esta compra ({productosCompra.length})
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {productosCompra.map((producto, index) => {
                      const material = materiales.find(m => m.id === producto.material_id);
                      return (
                        <div 
                          key={index}
                          className="flex items-center justify-between bg-gray-50 p-3 rounded border"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{producto.material_nombre}</p>
                            <p className="text-xs text-gray-600">
                              Cantidad: {producto.cantidad} | 
                              Costo Unit: ${producto.costo_unitario?.toFixed(2) || '0.00'} | 
                              Total: ${producto.costo_total?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleEliminarProducto(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-sm font-bold text-right">
                      Total de la Compra: ${productosCompra.reduce((sum, p) => sum + (p.costo_total || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                // Limpiar todos los estados al cancelar
                setProductosCompra([]);
                setProductoActual({
                  material_id: '',
                  cantidad: 0,
                  costo_unitario: 0,
                  costo_total: 0,
                });
                setEntradaForm({
                  material_id: '',
                  cantidad: 0,
                  proveedor: '',
                  costo_unitario: 0,
                  costo_total: 0,
                  fecha_entrada: new Date().toISOString().split('T')[0],
                  numero_factura: '',
                  notas: '',
                  usuario_registro: user.full_name || user.username,
                });
                setEntradaDialogOpen(false);
              }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEntrada} 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={productosCompra.length === 0}
              >
                Registrar Compra ({productosCompra.length} producto{productosCompra.length !== 1 ? 's' : ''})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
}
